<template>
    <div class="card">
        <PageHeader
            :showBtn="false"
            title="Team Task Management"
        />

        <div class="filter-section">
            <div class="filter-bar">
                <div class="week-nav">
                    <button class="btn week-nav-btn" @click="prevWeek">
                        <i class="fa-solid fa-chevron-left"></i>
                    </button>
                    <span class="week-nav-label">{{ weekRangeText }}</span>
                    <button class="btn week-nav-btn" @click="nextWeek">
                        <i class="fa-solid fa-chevron-right"></i>
                    </button>
                </div>

                <FilterPopover
                    ref="filterPopoverRef"
                    :active-count="activeFilterCount"
                    @open="onOpenFilter"
                    @apply="onApplyFilters"
                    @clear="onClearFilters"
                >
                    <div class="col-12">
                        <label class="filter-label">Team Member</label>
                        <AsyncSelect
                            placeholder="Any team member"
                            v-model="draftFilters.contactProfile"
                            :api-service="fetchContactProfiles"
                            :additional-query="{roles: 'employee', status: 'active'}"
                            label="name"
                        />
                    </div>

                    <div class="col-12 col-sm-6">
                        <label class="filter-label">Activity Group</label>
                        <AsyncSelect
                            placeholder="Any group"
                            v-model="draftFilters.activityGroup"
                            :additional-query="{type: 'group'}"
                            :api-service="fetchActivitySetupEndpoint"
                            :option-selected="onChangeActivityGroup"
                            label="name"
                        />
                    </div>

                    <div class="col-12 col-sm-6">
                        <label class="filter-label">Organization Activity</label>
                        <AsyncSelect
                            placeholder="Any activity"
                            v-model="draftFilters.organizationActivity"
                            :api-service="fetchActivitySetupEndpoint"
                            label="name"
                            :additional-query="{ group_id: draftFilters.activityGroup && draftFilters.activityGroup.id }"
                            ref="organizationActivityDropdown"
                        />
                    </div>
                </FilterPopover>
            </div>

            <div v-if="activeFilterChips.length" class="filter-chips">
                <span v-for="chip in activeFilterChips" :key="chip.key" class="filter-chip">
                    <span class="filter-chip-label">{{ chip.label }}:</span>
                    {{ chip.text }}
                    <button type="button" class="filter-chip-remove" @click="removeFilter(chip.key)">
                        <i class="fa-solid fa-xmark"></i>
                    </button>
                </span>
                <button type="button" class="filter-chip-clear" @click="onClearFilters">Clear all</button>
            </div>
        </div>

      <div class="table-section">
        <div class="week-table-card">
          <div class="table-responsive">
            <table class="table table-bordered">
              <thead>
              <tr>
                <th>Team Member</th>
                <th v-for="(day, i) in weekDays" :key="i">
                  <span class="week-day-name">{{ formatDateToDay(day) }}</span>
                  <span class="week-day-date">{{ formatDayToDateReadable(day) }}</span>
                </th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, i) in tableItems" :key="i">
                <td class="member-name-cell">{{ item.name }}</td>
                <td v-for="(day, j) in weekDays" :key="j" class="week-day-cell">
                  <div v-if="item.days[formatISODate(day)] && item.days[formatISODate(day)].length">
                      <div
                          v-for="(activity, k) in item.days[formatISODate(day)]"
                          @click="goToEdit(activity.id)"
                          :key="k"
                          class="activity-card"
                          :title="activity.short_description"
                          :style="{
                            backgroundColor: getTaskStatusColor(activity.status).bg,
                            color: getTaskStatusColor(activity.status).text,
                          }"
                      >
                      <span class="activity-type">{{ activity.type }}</span>
                      <span class="activity-title">{{ activity.short_description }}</span>
                    </div>
                  </div>
                  <span v-else class="week-day-empty">&mdash;</span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <div v-if="!tableItems.length && !loading" class="week-empty">
            <i class="fa-regular fa-calendar week-empty-icon"></i>
            <p class="week-empty-title">No tasks this week</p>
            <p class="week-empty-text">Try another week or adjust the filters.</p>
          </div>
        </div>
      </div>

      <TaskEventScheduler
          v-if="store.state.isModalOpen"
          :startDate="startDate"
          :endDate="endDate"
          :editData="singleActivity"
          :taskStatus="taskStatus"
          :markAsDoneShow="markAsDoneShow"
          :isCompleted="isCompleted"
          @submitted="onEventSubmitted"
      />

     <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import PageHeader from '@/components/atom/PageHeader.vue';
import FilterPopover from '@/components/atom/FilterPopover.vue';
import { computed, inject, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import handleActivity from "@/services/modules/procurement/activity";
import handleContact from '@/services/modules/contact'
import handleDropDowns from '@/services/modules/dropdowns'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import TaskEventScheduler from '@/components/molecule/company/admin/TaskEventScheduler.vue';
import { formatDayToDateReadable, formatDateToDay, formatISODate, getStartOfWeek, addDays, getTaskStatusColor } from '@/services/utils/global.js';

const store = useStore();
const route = useRoute();
const showError = inject('showError');
const loading = ref(false)
const tableItems = ref([]);
const singleActivity = ref(null);
const taskStatus = ref({});
const markAsDoneShow = ref(false);
const isCompleted = ref(false);
const organizationActivityDropdown = ref(null)
const filterPopoverRef = ref(null);

const getEmptyFilters = () => {
    return {
        contactProfile: '',
        activityGroup: '',
        organizationActivity: ''
    };
}

const appliedFilters = ref(getEmptyFilters());
const draftFilters = ref(getEmptyFilters());

const filterChipMeta = {
    contactProfile: 'Team Member',
    activityGroup: 'Activity Group',
    organizationActivity: 'Organization Activity'
};

const { fetchTeamTaskManagementList, fetchSingleTaskActivityLog } = handleActivity()
const { fetchContactProfiles } = handleContact()
const { fetchActivitySetupEndpoint } = handleDropDowns();

const getChipText = (value) => {
    if (typeof value === 'object') return value.name
    return value
}

const activeFilterChips = computed(() => {
    return Object.keys(filterChipMeta).filter(key => {
        return !!appliedFilters.value[key];
    }).map(key => {
        return {
            key: key,
            label: filterChipMeta[key],
            text: getChipText(appliedFilters.value[key])
        };
    });
})

const activeFilterCount = computed(() => {
    return activeFilterChips.value.length;
})

const onOpenFilter = () => {
    draftFilters.value = Object.assign({}, appliedFilters.value);
}

const onApplyFilters = () => {
    appliedFilters.value = Object.assign({}, draftFilters.value);
    getTaskManagementList();
}

const onClearFilters = () => {
    appliedFilters.value = getEmptyFilters();
    draftFilters.value = getEmptyFilters();
    if (filterPopoverRef.value) filterPopoverRef.value.close();
    getTaskManagementList();
}

const removeFilter = (key) => {
    appliedFilters.value[key] = '';
    draftFilters.value[key] = '';
    getTaskManagementList();
}

const onChangeActivityGroup = () => {
  draftFilters.value.organizationActivity = null;
  if (! organizationActivityDropdown.value) return
  organizationActivityDropdown.value.clear();
}

const onEventSubmitted = () => {
  getTaskManagementList();
};

const today = new Date();
const startDate = ref(getStartOfWeek(today));
const endDate = ref(addDays(getStartOfWeek(today), 6));

const weekDays = computed(() => {
  const days = [];
  for (let i = 0; i < 7; i++) {
    days.push(addDays(startDate.value, i));
  }
  return days;
});

const weekRangeText = computed(() => {
  return `${formatDateToDay(startDate.value)} (${formatDayToDateReadable(startDate.value)}) - ${formatDateToDay(endDate.value)} (${formatDayToDateReadable(endDate.value)})`;
});

async function nextWeek() {
  startDate.value = addDays(startDate.value, 7);
  endDate.value = addDays(endDate.value, 7);
  await getTaskManagementList();
}

async function prevWeek() {
  startDate.value = addDays(startDate.value, -7);
  endDate.value = addDays(endDate.value, -7);
  await getTaskManagementList();
}

const goToEdit = async (activityId) =>  {
  if (!activityId) return;
  markAsDoneShow.value = false;
  isCompleted.value = false;
  loading.value = true;

  try {
    const res = await fetchSingleTaskActivityLog(activityId, getQuery());
    if (!res.status) return showError(res.message);
    let response = res.data;
    singleActivity.value = response;
    taskStatus.value = res.task_status;
    markAsDoneShow.value = true;
    if (['completed', 'approved'].includes(response.status)) {
      isCompleted.value = true;
    }
    store.state.isModalOpen = true;
  } finally {
    loading.value = false;
  }
}

const getTaskManagementList = async () => {
    const query = getQuery();
    loading.value = true
    tableItems.value = []

    await fetchTeamTaskManagementList(query).then(res => {
        if (!res.status){
            return
        }
        tableItems.value = res.data;
    }).catch(err => {
        showError(err.message)
    }).finally(() => {
        loading.value = false
    })
}

const getQuery = () => {
    const filters = appliedFilters.value;
    let query = `?company_id=${route.params.companyId}&route_name=task-calendar`;
    query += '&start_date=' + formatISODate(startDate.value);
    query += '&end_date=' + formatISODate(endDate.value);
    if (filters.organizationActivity) query += `&organization_activity=${filters.organizationActivity.id}`;
    if (filters.activityGroup) query += `&activity_group=${filters.activityGroup.id}`;
    if (filters.contactProfile) query += `&contact_profile_id=${filters.contactProfile.id}`;
    return query;
}

onMounted(() => {
    Promise.all([
        getTaskManagementList(),
    ])
})

</script>

<style scoped>
.filter-section {
    padding: 16px 20px 0;
}
.filter-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 12px;
}
.filter-label {
    display: block;
    margin-bottom: 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #101828;
}
.week-nav {
    display: flex;
    align-items: center;
    gap: 8px;
}
.week-nav-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: 1px solid #7367f0;
    border-radius: 8px;
    background: #fff;
    color: #7367f0;
}
.week-nav-btn:hover {
    background: #7367f0;
    color: #fff;
}
.week-nav-label {
    font-size: 13px;
    font-weight: 600;
    color: #101828;
    white-space: nowrap;
}
.filter-chips {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
}
.filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 8px 4px 10px;
    border-radius: 16px;
    background: #f4f3ff;
    color: #4338ca;
    font-size: 12px;
}
.filter-chip-label {
    color: #7c78a8;
}
.filter-chip-remove {
    border: 0;
    background: transparent;
    color: #7c78a8;
    font-size: 11px;
    line-height: 1;
    padding: 2px;
    cursor: pointer;
}
.filter-chip-remove:hover {
    color: #4338ca;
}
.filter-chip-clear {
    border: 0;
    background: transparent;
    color: #667085;
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;
}
.table-section {
    padding: 16px 20px 20px;
}
.week-table-card {
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 12px;
    overflow: hidden;
    min-height: 200px;
}
.week-table-card :deep(.table) {
    margin-bottom: 0;
}
.week-table-card :deep(.table-bordered),
.week-table-card :deep(.table-bordered th),
.week-table-card :deep(.table-bordered td) {
    border-color: #f2f4f7;
}
.week-table-card :deep(.table-bordered > :not(caption) > *) {
    border-width: 0;
}
.week-table-card :deep(thead th) {
    padding: 10px 12px;
    background: #f9fafb;
    border-bottom: 1px solid #eaecf0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #101828;
    white-space: nowrap;
}
.week-table-card :deep(tbody td) {
    padding: 8px 12px;
    font-size: 13px;
    color: #344054;
    vertical-align: top;
    border-bottom: 1px solid #f2f4f7;
}
.week-day-name {
    display: block;
}
.week-day-date {
    display: block;
    margin-top: 2px;
    font-weight: 500;
    color: #667085;
    text-transform: none;
    letter-spacing: 0;
}
.member-name-cell {
    min-width: 150px;
    font-weight: 600;
    color: #101828;
    white-space: nowrap;
}
.week-day-cell {
    min-width: 150px;
}
.week-day-empty {
    color: #d0d5dd;
}
.activity-card {
    padding: 6px 8px;
    margin-bottom: 6px;
    border-radius: 8px;
    cursor: pointer;
    transition: filter .15s ease;
}
.activity-card:last-child {
    margin-bottom: 0;
}
.activity-card:hover {
    filter: brightness(.95);
}
.activity-type {
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    opacity: .75;
}
.activity-title {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    font-weight: 500;
}
.week-empty {
    padding: 48px 16px;
    text-align: center;
}
.week-empty-icon {
    font-size: 30px;
    color: #d0d5dd;
}
.week-empty-title {
    margin: 12px 0 2px;
    font-size: 15px;
    font-weight: 600;
    color: #344054;
}
.week-empty-text {
    margin-bottom: 0;
    font-size: 13px;
    color: #98a2b3;
}
</style>
